import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Table from "../../../shared/Table";
import { COLUMN_DEFINITIONS } from "./table.config";
import {
  useUpdateManifestLegMutation,
  useUpdateManifestMutation,
} from "../../../redux/services/manifests/manifestsApi";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import { toast } from "react-toastify";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { flexRender } from "@tanstack/react-table";
import classNames from "classnames";
import Cell from "../../../shared/Table/Cell";
import { Bars2Icon } from "@heroicons/react/24/outline";

const JobLegs = ({
  info,
  form = {},
  visible = false,
  setVisible,
  ...props
}) => {
  const { hasPermission } = usePermissions();
  const canReorder = hasPermission(PERMISSIONS.MANIFESTS_EDIT);
  const dndType = "manifest-leg-row";

  const legs = useMemo(() => {
    return (
      info.legs?.map((item) => {
        const data = {
          ...item,
          satus: item.status,
          jobId: item.jobId,
        };
        return {
          ...data,
          subItem: [data],
        };
      }) || []
    );
  }, [info.legs]);

  const [orderedLegs, setOrderedLegs] = useState(legs);
  const orderedLegsRef = useRef(legs);
  const lastSavedOrderRef = useRef([]);

  useEffect(() => {
    setOrderedLegs(legs);
    orderedLegsRef.current = legs;
    lastSavedOrderRef.current = legs.map((l) => String(l._id));
  }, [info._id, legs]);

  const [updateManifestLeg] = useUpdateManifestLegMutation();
  const [updateManifest, { isLoading: isSavingOrder }] =
    useUpdateManifestMutation();

  function handleEditLeg(e, jobData) {
    const updateItem = {
      ...e,
      client: jobData?.client,
      origin: e.route?.origin,
      destination: e.route?.destination,
      totalMiles: e.route?.mileage,
      customMileage: e.route?.customMileage,
      useCustomMileage: !!e.route?.customMileage,
      appointmentDate: e.appointmentDate,
      appointmentTime: e.appointment?.time,
      totalWaitTime: e.waitTime?.total,
      waitStart: e.waitTime?.start,
      waitEnd: e.waitTime?.end,
      base: jobData.base || jobData.baseRate,
      fuelRate: jobData.fuelRate,
      orderNumber: e.orderNumber,
      useLegFlatRate: jobData.useFlatRate || e.useLegFlatRate,
      job: jobData,
      edit: true,
    };
    form.setFormInit(updateItem, { setErrors: true });
    setVisible(true);
  }

  function onModalCancel() {
    form.clearForm();
    setVisible(false);
  }

  const moveRow = useCallback((fromIndex, toIndex) => {
    setOrderedLegs((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      orderedLegsRef.current = next;
      return next;
    });
  }, []);

  const persistSortOrder = useCallback(
    async (nextLegIds) => {
      if (!info?._id) return;
      if (!Array.isArray(nextLegIds) || nextLegIds.length === 0) return;

      const prevIds = lastSavedOrderRef.current || [];
      const isSame =
        prevIds.length === nextLegIds.length &&
        prevIds.every((id, idx) => id === nextLegIds[idx]);
      if (isSame) return;

      try {
        const res = await updateManifest({
          id: info._id,
          data: { manifestSortOrder: nextLegIds },
        }).unwrap();

        lastSavedOrderRef.current = nextLegIds;
        toast(res.message || "Updated manifest sort order", {
          type: res.status || "success",
          toastId: `manifest-sort-order-${info._id}`,
        });
      } catch (err) {
        toast(err?.data?.message || "Failed to update manifest sort order", {
          type: "error",
          toastId: `manifest-sort-order-error-${info._id}`,
        });

        // Roll back UI order to last saved order if possible
        setOrderedLegs((prev) => {
          const map = new Map(prev.map((l) => [String(l._id), l]));
          const restored = (lastSavedOrderRef.current || [])
            .map((id) => map.get(String(id)))
            .filter(Boolean);
          return restored.length ? restored : prev;
        });
      }
    },
    [info?._id, updateManifest]
  );

  async function handleUpdate(e, setLoading) {
    const data = {
      ...e.data,
    };

    if (e.column.id === "route") {
      const route = e.row.original.route;
      // e.value should be the mileageData object from MileageSelector
      // e.data.route will also contain the mileageData object
      let mileageData = e.value || e.data?.route;
      let selectedValue = null;
      let isRouteMileage = false;
      let mileageType = null;

      // Check if we have our mileageData object (has isRouteMileage property)
      if (
        mileageData &&
        typeof mileageData === "object" &&
        "isRouteMileage" in mileageData
      ) {
        // This is from MileageSelector
        selectedValue = mileageData.value;
        isRouteMileage = mileageData.isRouteMileage;
        mileageType = mileageData.mileageType;
      } else {
        // Fallback: e.value might be the route object from getValue()
        // This shouldn't happen if MileageSelector is working correctly
        // But if it does, we can't determine the selection, so don't update
        console.warn("Unable to determine mileage selection:", {
          value: e.value,
          dataRoute: e.data?.route,
          mileageData,
        });
        setLoading(false);
        return;
      }

      // Process the selection
      // Store which mileage type is being used, preserving user intent
      if (typeof selectedValue === "number" && selectedValue > 0) {
        // Store the mileage type so we know which route field was selected
        data.useCustomManifestMileage = true;
        data.manifestMileageType = mileageType;

        if (mileageType === "customInput") {
          // For custom input, just store the value in customManifestMileage
          data.customManifestMileage = selectedValue;
        } else {
          // For route options, store the value for backward compatibility
          // but the type tells us which route field to use
          data.customManifestMileage = selectedValue;
        }
      } else if (selectedValue === "customInput") {
        // Custom input option selected but no value yet
        data.useCustomManifestMileage = true;
        data.manifestMileageType = "customInput";
        if (
          e.row.original.useCustomManifestMileage &&
          e.row.original.customManifestMileage != null
        ) {
          data.customManifestMileage = e.row.original.customManifestMileage;
        } else {
          data.customManifestMileage = 0;
        }
      }
    }

    delete data.route;

    const updateData = {
      // parent: user.parentCompany,
      legId: e.row.original._id,
      id: info._id,
      data,
    };

    const res = await updateManifestLeg(updateData).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: "updated-leg-" + res.data._id,
    });

    if (res.status === "success") {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }

  const ManifestLegRow = ({ row, index, TableRow, rowClass, onRowClick }) => {
    const rowRef = useRef(null);
    const handleRef = useRef(null);

    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: dndType,
        item: { id: row.id, index },
        canDrag: canReorder && !isSavingOrder,
        end: () => {
          const nextIds = (orderedLegsRef.current || []).map((l) =>
            String(l._id)
          );
          persistSortOrder(nextIds);
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [row.id, index, canReorder, isSavingOrder, orderedLegs, persistSortOrder]
    );

    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: dndType,
        hover: (item, monitor) => {
          if (!rowRef.current) return;
          if (!canReorder || isSavingOrder) return;

          const dragIndex = item.index;
          const hoverIndex = index;
          if (dragIndex === hoverIndex) return;

          const hoverBoundingRect = rowRef.current.getBoundingClientRect();
          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const clientOffset = monitor.getClientOffset();
          if (!clientOffset) return;
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;

          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

          moveRow(dragIndex, hoverIndex);
          item.index = hoverIndex;
        },
        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
        }),
      }),
      [index, canReorder, isSavingOrder, moveRow]
    );

    drop(rowRef);

    const setHandleNode = (node) => {
      handleRef.current = node;
      if (node) drag(node);
    };

    const dragRowClass = classNames(rowClass, {
      "opacity-70": isDragging,
    });

    // Only highlight during drag/reorder interactions (no "selected row" state).
    const rowHighlight = {
      "bg-secondary-50": isOver || isDragging,
      "border-secondary-200": isOver || isDragging,
    };

    return (
      <TableRow
        ref={rowRef}
        onClick={onRowClick}
        className={dragRowClass}
        style={{ cursor: canReorder ? "default" : undefined }}
      >
        {row.getVisibleCells().map((cell) => {
          const isCheckbox = !props.hideCheckbox && cell.column.id === "select";
          const classes = classNames(
            "whitespace-no-wrap border-b border-gray-200 transition-colors",
            rowHighlight,
            {
              "w-0 max-w-0 border-r": isCheckbox,
            }
          );

          if (cell.column.id === "drag") {
            return (
              <td
                key={cell.id}
                className={classes}
                style={{ width: cell.column.getSize?.() || 40 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-center p-2">
                  <button
                    type="button"
                    ref={setHandleNode}
                    disabled={!canReorder || isSavingOrder}
                    title={
                      canReorder
                        ? "Drag to reorder legs"
                        : "Not authorized to reorder"
                    }
                    className={classNames("p-1 rounded text-gray-400", {
                      "cursor-grab hover:text-gray-600 hover:bg-gray-50":
                        canReorder && !isSavingOrder,
                      "cursor-not-allowed opacity-60":
                        !canReorder || isSavingOrder,
                    })}
                  >
                    <Bars2Icon className="h-4 w-4" />
                  </button>
                </div>
              </td>
            );
          }

          const content = flexRender(
            cell.column.columnDef.cell,
            cell.getContext()
          );

          return (
            <td key={cell.id} className={classes}>
              {isCheckbox ? (
                <span className="flex justify-center">{content}</span>
              ) : (
                <Cell updateData={handleUpdate} {...cell}>
                  {content}
                </Cell>
              )}
            </td>
          );
        })}
      </TableRow>
    );
  };

  return (
    <>
      {canReorder ? (
        <DndProvider backend={HTML5Backend}>
          <Table
            pageSize={100}
            data={orderedLegs}
            columns={COLUMN_DEFINITIONS}
            hideCheckbox
            enableSorting={false}
            getRowId={(row) => row._id}
            RowComponent={ManifestLegRow}
            updateData={handleUpdate}
            meta={{
              updateData: handleUpdate,
            }}
          />
        </DndProvider>
      ) : (
        <Table
          pageSize={100}
          data={orderedLegs}
          columns={COLUMN_DEFINITIONS}
          hideCheckbox
          enableSorting={false}
          getRowId={(row) => row._id}
          updateData={handleUpdate}
          meta={{
            updateData: handleUpdate,
          }}
        />
      )}

      {/* <LegCreateModal
        visible={visible}
        setVisible={setVisible}
        onCancel={onModalCancel}
        form={form}
        job={legs.jobId}
      /> */}
    </>
  );
};

export default JobLegs;
